"use client";

import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import classNames from "classnames";

interface BlobsProps {
  variant?: "hero" | "projects";
  className?: string;
}

export default function Blobs({ variant = "hero", className }: BlobsProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // 1. Global scroll for Hero
  const { scrollY } = useScroll();

  // 2. Target scroll for Projects
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  // --- HERO PARALLAX ---
  const rawHeroY1 = useTransform(scrollY, [0, 3000], [0, 2000]); 
  const rawHeroY2 = useTransform(scrollY, [0, 3000], [0, 1000]);

  // --- PROJECTS PARALLAX ---
  const rawProjectsY1 = useTransform(scrollYProgress, [0, 1], [-500, 800]);
  const rawProjectsY2 = useTransform(scrollYProgress, [0, 1], [500, -400]);

  // --- SMOOTHING ---
  const springConfig = { stiffness: 50, damping: 20, restDelta: 0.001 };
  
  const selectedY1 = variant === "hero" ? rawHeroY1 : rawProjectsY1;
  const selectedY2 = variant === "hero" ? rawHeroY2 : rawProjectsY2;

  const y1 = useSpring(selectedY1, springConfig);
  const y2 = useSpring(selectedY2, springConfig);

  return (
    <div 
      ref={containerRef} 
      data-comment="bg-blobs"
      className={classNames(
        "absolute inset-0 z-0 overflow-visible pointer-events-none isolate", 
        className
      )}
    >
      <div className="absolute inset-0 w-full h-full opacity-60 dark:opacity-20 overflow-visible">
        
        {/* BLOB 1 (Red/Beige) */}
        <motion.div 
          style={{ y: y1 }}
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.6, 0.8, 0.6],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className={classNames(
            "absolute rounded-full filter mix-blend-multiply dark:mix-blend-screen blur-[80px] md:blur-[120px] bg-red/40 dark:bg-beige will-change-transform [transform:translateZ(0)]",
            {
              "top-10 -left-32 w-[300px] h-[300px] md:w-[500px] md:h-[500px]": variant === "hero",
              "top-40 -right-32 w-[400px] h-[400px] md:w-[600px] md:h-[600px]": variant === "projects",
            }
          )}
        />
        
        {/* BLOB 2 (Green) */}
        <motion.div 
          style={{ y: y2 }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.5, 0.7, 0.5],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2,
          }}
          className={classNames(
            "absolute rounded-full filter mix-blend-multiply dark:mix-blend-screen blur-[80px] md:blur-[120px] bg-green/80 dark:bg-green/40 will-change-transform [transform:translateZ(0)]",
            {
              "top-[30%] -right-32 w-[400px] h-[400px] md:w-[600px] md:h-[600px]": variant === "hero",
              "bottom-20 -left-40 w-[300px] h-[300px] md:w-[500px] md:h-[500px]": variant === "projects",
            }
          )}
        />
      </div>
    </div>
  );
}